import React, { useContext, useEffect } from 'react';
import { PageWrapper, getApiConfiguration } from '../../common';
import { Flexbox } from '@hiiretail/synergy-ui';
import { useGetProfile } from './hooks/useGetProfile';
import { UserProfileApi, UsersV2Api } from '../../api-client';
import { Spinner } from '../../common/components/Spinner/Spinner';
import { MyPermissionsList } from './ui/MyPermissionsList/MyPermissionsList';
import { useGetOwnPermissions } from './hooks/useGetOwnPermissions';
import { useResetOwnPassword } from './hooks/useResetOwnPassword';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { ResetOwnPasswordForm } from './ui/ResetOwnPasswordForm/ResetOwnPasswordForm';
import { GeneralInfoForm } from './ui/GeneralInfoForm/GeneralInfoForm';
import { useUpdateOwnProfileV2 } from './hooks/useUpdateOwnProfileV2';
import { useQueryClient } from 'react-query';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';

export const Profile: React.FC = () => {
  const profileApi = new UserProfileApi(getApiConfiguration());
  const usersV2Api = new UsersV2Api(getApiConfiguration());
  const { showNotification } = useContext(NotificationsContext);
  const queryClient = useQueryClient();

  const { updateActions } = useBreadcrumbsActions();

  useEffect(() => {
    updateActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    data: userProfile,
    isFetching: isProfileLoading,
    error: profileLoadingError,
    // refetch: refreshProfile,
  } = useGetProfile(profileApi, { keepPreviousData: false });

  const {
    data: effectivePermissions,
    isFetching: isPermissionsLoading,
    refetch: refetchPermissions,
    error: permissionsError,
  } = useGetOwnPermissions(profileApi, { keepPreviousData: false });

  const {
    mutate: resetOwnPassword,
    isLoading: isResetting,
    error: updateOwnPasswordError,
  } = useResetOwnPassword(profileApi, {
    onSuccess: () => {
      showNotification('Password changed successfully');
    },
  });

  const {
    mutate: updateOwnProfile,
    isLoading: isUpdatingOwnProfile,
    error: updateOwnProfileError,
  } = useUpdateOwnProfileV2(usersV2Api, {
    onSuccess: () => {
      showNotification('Information updated successfully');
      queryClient.invalidateQueries('userProfile').then();
    },
  });

  return (
    <PageWrapper>
      {profileLoadingError && <span>Error</span>}
      {isProfileLoading && <Spinner />}
      {!isProfileLoading && (
        <>
          <Flexbox container style={{ height: '100%' }} justifyContent="space-between">
            {userProfile && (
              <Flexbox container style={{ width: '30%' }}>
                <GeneralInfoForm
                  user={userProfile}
                  error={updateOwnProfileError}
                  isUpdating={isUpdatingOwnProfile}
                  updateProfile={(updateUserDto) =>
                    updateOwnProfile({
                      updateUserDto,
                      userId: userProfile.id,
                    })
                  }
                />
              </Flexbox>
            )}
            <Flexbox container style={{ width: '30%' }}>
              <MyPermissionsList
                permissions={effectivePermissions || []}
                isLoading={isPermissionsLoading}
                refetchPermissions={refetchPermissions}
                error={permissionsError}
              />
            </Flexbox>
            <Flexbox container style={{ width: '30%' }}>
              <ResetOwnPasswordForm
                error={updateOwnPasswordError}
                isResetting={isResetting}
                resetOwnPassword={resetOwnPassword}
              />
            </Flexbox>
          </Flexbox>
        </>
      )}
    </PageWrapper>
  );
};
